import { Suspense } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { ClientSelectionProvider, useClientSelection } from '../contexts/ClientSelectionContext';
import Header from './navigation/Header';
import { CardSkeleton } from './ui/Skeleton';
import { shellContainer } from './navigation/shell';
import { HelpProvider } from '../contexts/HelpContext';
import { useEnabledModules } from '../hooks/useEnabledModules';
import { getProfile } from '../lib/api';

export default function Layout() {
  return (
    <ClientSelectionProvider>
      <HelpProvider>
        <LayoutInner />
      </HelpProvider>
    </ClientSelectionProvider>
  );
}

function LayoutInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { effectiveClientId } = useClientSelection();
  const { enabledModules } = useEnabledModules();
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    staleTime: 5 * 60 * 1000,
  });

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Horizontal Header Navigation */}
      <Header
        isAdmin={user?.isAdmin || false}
        enabledModules={enabledModules}
        user={{
          email: user?.email,
          companyName: profile?.companyName || user?.companyName,
          contactName: profile?.contactName || user?.contactName,
        }}
        onLogout={handleLogout}
      />

      {/* Page Content */}
      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 pt-5 pb-6 md:pt-7 md:pb-8 overflow-auto">
          <div className={shellContainer(location.pathname)}>
            {/* Keyed on the acting client too: switching client remounts the page,
                so stale form state (addresses, products) can never cross clients.
                Admin management pages and the profile ignore the selection, so
                they keep their state across a switch. */}
            <Suspense fallback={<CardSkeleton />}>
              <Outlet
                key={
                  location.pathname.startsWith('/admin') || location.pathname.startsWith('/profile')
                    ? location.pathname
                    : `${location.pathname}:${effectiveClientId ?? ''}`
                }
              />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
